import { create } from 'zustand';

/**
 * Which Launcher V2 push drawers are currently open, and the main-content
 * push padding derived from that.
 */

/** Data-only, per the store convention (root CLAUDE.md, "Zustand for
 *  Module-Scope Client State"). */
interface PlaybooksDrawerState {
  openDrawerIds: readonly string[];
}

/** Exported per the store convention — the reset idiom is
 *  `usePlaybooksDrawerStore.setState(initialState, true)`. */
export const initialState: PlaybooksDrawerState = {
  openDrawerIds: [],
};

export const usePlaybooksDrawerStore = create<PlaybooksDrawerState>(() => ({ ...initialState }));

export function registerOpenDrawer(drawerId: string): void {
  usePlaybooksDrawerStore.setState(prev =>
    prev.openDrawerIds.includes(drawerId)
      ? prev
      : { openDrawerIds: [...prev.openDrawerIds, drawerId] },
  );
}

export function unregisterOpenDrawer(drawerId: string): void {
  usePlaybooksDrawerStore.setState(prev =>
    prev.openDrawerIds.includes(drawerId)
      ? { openDrawerIds: prev.openDrawerIds.filter(id => id !== drawerId) }
      : prev,
  );
}

export function selectIsAnyDrawerOpen(state: PlaybooksDrawerState): boolean {
  return state.openDrawerIds.length > 0;
}

/**
 * The panel's own width. 480px — `CampaignDetailSidebar`'s dimension, kept as
 * the A2 starting point (a Terra dimensional call under the Glaze adoption
 * plan's carve-out: no design-system sign-off needed, per-surface tuning
 * happens in the units that mount drawers).
 */
export const PUSH_DRAWER_WIDTH_CLASS = 'w-[30rem]';

/**
 * The drawer's stacking context. This is where the z-index lives — NEVER on
 * the Popup (`PUSH_DRAWER_POPUP_CLASS` below carries no `z-*` class at all).
 *
 * Base UI nests a child portal opened INSIDE this drawer (a Glaze `Combobox` /
 * `ComboboxMultiple` positioner) into the drawer's own portal node rather than
 * `document.body`. If the z-index sat on the Popup, the popup and its nested
 * child portal would share the SAME stacking context and the popup — painted
 * later — would cover the child. Putting `z-50` on the Portal instead still
 * out-ranks the app chrome, and `relative` gives the z-index something to
 * apply to (the node has no in-flow children, so it stays zero-height and
 * blocks no clicks). See `PushDrawer.tsx`'s stacking-contract doc block.
 */
export const PUSH_DRAWER_PORTAL_CLASS = 'relative z-50';

/**
 * The panel's own visual treatment — identical to the class that used to sit
 * directly on the Popup, MINUS `z-50` (which moved to `PUSH_DRAWER_PORTAL_CLASS`
 * above). Never add a `z-*` class back here.
 */
export const PUSH_DRAWER_POPUP_CLASS = `fixed inset-y-0 right-0 flex ${PUSH_DRAWER_WIDTH_CLASS} max-w-full flex-col border-l border-border-primary bg-bg-primary shadow-lg focus:outline-0 transition-all duration-200 ease-out data-starting-style:translate-x-3 data-starting-style:opacity-0 motion-reduce:transition-none motion-reduce:data-starting-style:translate-x-0`;

/**
 * The main-content padding that makes the page REFLOW around an open panel
 * instead of being covered by it: the panel's 30rem plus a 1.5rem gutter.
 *
 * This is the prototype's `body[data-sidebar] .app-main{padding-right:28.5rem}`
 * (27rem panel + 1.5rem gutter) translated to React state at Terra's panel
 * width. The prototype's companion
 * `@media (max-width:1400px){...padding-right:1.5rem}` overlay fallback is
 * DELIBERATELY NOT PORTED — Decision 4 is push at ALL widths, so this class
 * carries no breakpoint prefix. Do not add one.
 */
export const MAIN_CONTENT_PUSH_CLASS = 'pr-[31.5rem]';

export function mainContentPushClassName(isPushed: boolean): string {
  return isPushed ? MAIN_CONTENT_PUSH_CLASS : '';
}
